import { MessageSquare, FileText, Database, GitBranch, BookOpen, Settings, Bell, User, LogOut, Coins, ChevronDown, ListTodo, MoreVertical, Folder, MessageCircle, Info, HelpCircle, FileEdit, Lock, Sparkles, ClipboardList, LifeBuoy, FileOutput, CalendarClock, Network, PenLine, ShieldCheck, ReceiptText } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useCompactMode } from '@/hooks/useCompactMode';
import { useDocketCount } from '@/hooks/useDocketCount';
import { usePremium } from '@/hooks/usePremium';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { CreditsModal } from './CreditsModal';
import { SettingsModal } from './SettingsModal';
import { FeedbackModal } from './FeedbackModal';
import { AboutModal } from './AboutModal';
import { UserGuide } from './UserGuide';
import { SupportRequestPanel } from './SupportRequestPanel';
import { NotificationDropdown } from './NotificationDropdown';
import { useNotificationStore } from '@/stores';
import { creditApi } from '@/services/api';
import { isAdminUsername } from '@/utils/admin';
import logoIcon from '@/assets/loop-icon.png';

interface HeaderProps {
  activePage: 'chat' | 'documents' | 'uspto_data' | 'examination' | 'resources' | 'tasks' | 'sessions' | 'drafts' | 'ids' | 'forms' | 'templates';
  onPageChange: (page: 'chat' | 'documents' | 'uspto_data' | 'examination' | 'resources' | 'tasks' | 'drafts' | 'ids' | 'forms' | 'templates') => void;
}

export function Header({ activePage, onPageChange }: HeaderProps) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { compactMode: globalCompactMode } = useCompactMode();
  const { isPremium } = usePremium();
  const { addNotification } = useNotificationStore();
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showUserGuide, setShowUserGuide] = useState(false);
  const [showSupportPanel, setShowSupportPanel] = useState(false);
  const [credits, setCredits] = useState<number | null>(null);
  const [creditsLoading, setCreditsLoading] = useState(true);
  const [isCompactMode, setIsCompactMode] = useState(false);
  const [isCompactNav, setIsCompactNav] = useState(false);
  // Before the nav collapses entirely, tighten it up: Quick Start drops to
  // icon-only and the nav buttons lose padding, so nothing squeezes the
  // logo/BETA tag on narrower screens
  const [isTightNav, setIsTightNav] = useState(false);

  const { open: docketOpen, overdue: docketOverdue } = useDocketCount();

  const notifications = useNotificationStore(state => state.notifications);

  // Count only unread success notifications for the bell badge
  const unreadSuccessCount = notifications.filter(n => !n.read).length;

  // Fetch credits
  const fetchCredits = async () => {
    try {
      const response = await creditApi.getCreditBalance();
      if (response.success && response.remaining_credits !== undefined) {
        setCredits(response.remaining_credits);
      }
    } catch (error) {
      console.error('Failed to fetch credits:', error);
      // Don't show error to user, just silently fail
    } finally {
      setCreditsLoading(false);
    }
  };

  // Fetch credits on mount and listen for updates
  useEffect(() => {
    fetchCredits();

    // Listen for credit updates (triggered after chat messages, etc.)
    const handleCreditsUpdate = () => {
      fetchCredits();
    };

    window.addEventListener('credits-updated', handleCreditsUpdate);

    return () => {
      window.removeEventListener('credits-updated', handleCreditsUpdate);
    };
  }, []);

  // Track window width for compact modes
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsCompactMode(width < 1350);
      setIsCompactNav(width < 1150);
      setIsTightNav(width < 1750);
    };

    // Initial check
    handleResize();

    // Listen for resize events
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Helper to get page display info
  const getPageInfo = (page: typeof activePage) => {
    switch (page) {
      case 'chat':
        return { icon: MessageSquare, label: 'Chat' };
      case 'documents':
        return { icon: FileText, label: 'Documents' };
      case 'uspto_data':
        return { icon: Database, label: 'USPTO Data' };
      case 'examination':
        return { icon: GitBranch, label: 'Examination' };
      case 'resources':
        return { icon: BookOpen, label: 'Resources' };
      case 'tasks':
        return { icon: ListTodo, label: 'Tasks' };
      case 'ids':
        return { icon: ClipboardList, label: 'IDS Generator' };
      case 'forms':
        return { icon: FileText, label: 'USPTO Forms' };
      case 'templates':
        return { icon: FileOutput, label: 'OA Templates' };
      case 'sessions':
        return { icon: Folder, label: 'Sessions' };
      // Was missing, so the compact page selector read "Chat" while on the drafts page.
      case 'drafts':
        return { icon: FileEdit, label: 'My Drafts' };
      default:
        return { icon: MessageSquare, label: 'Chat' };
    }
  };

  const currentPageInfo = getPageInfo(activePage);
  const CurrentPageIcon = currentPageInfo.icon;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleOpenSettings = () => {
    // Small delay to ensure dropdown menu overlay is removed before opening modal
    setTimeout(() => {
      setShowSettingsModal(true);
    }, 100);
  };

  const handleOpenFeedback = () => {
    // Small delay to ensure dropdown menu overlay is removed before opening modal
    setTimeout(() => {
      setShowFeedbackModal(true);
    }, 100);
  };

  const handleOpenAbout = () => {
    // Small delay to ensure dropdown menu overlay is removed before opening modal
    setTimeout(() => {
      setShowAboutModal(true);
    }, 100);
  };

  const handleOpenUserGuide = () => {
    // Small delay to ensure dropdown menu overlay is removed before opening modal
    setTimeout(() => {
      setShowUserGuide(true);
    }, 100);
  };

  // The guide lives here, and other screens ask for it by event rather than by having it
  // threaded down to them — the same arrangement the quick start already uses to be opened.
  useEffect(() => {
    const open = () => setShowUserGuide(true);
    window.addEventListener('open-user-guide', open);
    return () => window.removeEventListener('open-user-guide', open);
  }, []);

  const handleOpenSupportPanel = () => {
    // Small delay to ensure dropdown menu overlay is removed before opening modal
    setTimeout(() => {
      setShowSupportPanel(true);
    }, 100);
  };

  const handleDraftsClick = () => {
    if (!isPremium) {
      addNotification({
        type: 'warning',
        title: 'Premium Feature',
        message: 'Drafts is a Premium-only feature. Please upgrade to Premium to access this functionality.',
        duration: 5000
      });
      return;
    }
    onPageChange('drafts');
  };

  /**
   * Drafts and Drafting, under one labelled menu.
   *
   * They were two unlabelled icon buttons side by side — a pencil-on-paper and a pen —
   * for two different things: the drafts you have saved, and the workspace that builds
   * an application from a disclosure. Nothing on screen said which was which, and the
   * icons did not help. A menu can carry the names and a line of explanation, which is
   * what the distinction actually needs. Docket stays a button of its own: its badge is
   * a live count, and a count inside a closed menu is no count at all.
   */
  const renderDraftingMenu = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={activePage === 'drafts' ? 'secondary' : 'ghost'}
          size="icon"
          aria-label="Drafting"
          title="Drafting — your drafts, and the application builder"
        >
          <PenLine className="w-5 h-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 bg-white border border-gray-200 shadow-lg">
        <DropdownMenuItem
          onClick={handleDraftsClick}
          className={`cursor-pointer ${activePage === 'drafts' ? 'bg-blue-50' : ''}`}
        >
          <FileEdit className="w-4 h-4 mr-2 mt-0.5 shrink-0 text-gray-600" />
          <span className="flex min-w-0 flex-col">
            <span className="flex items-center gap-1.5 font-medium text-gray-900">
              My Drafts
              {!isPremium && <Lock className="w-3 h-3 text-gray-400" />}
            </span>
            <span className="text-xs text-gray-500">Drafts you have saved</span>
          </span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/drafting')} className="cursor-pointer">
          <PenLine className="w-4 h-4 mr-2 mt-0.5 shrink-0 text-gray-600" />
          <span className="flex min-w-0 flex-col">
            <span className="font-medium text-gray-900">Drafting Workspace</span>
            <span className="text-xs text-gray-500">
              Build an application from a disclosure
            </span>
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const handleOpenQuickStart = () => {
    window.dispatchEvent(new Event('open-onboarding-wizard'));
  };

  // Shared sizing for the header nav buttons
  const navButtonClass = globalCompactMode
    ? 'gap-1 text-xs px-2'
    : isTightNav
      ? 'gap-1.5 text-sm px-2'
      : 'gap-2';

  return (
    <header className={`border-b border-slate-200 bg-white transition-all ${globalCompactMode ? 'px-2 py-1' : 'px-4 py-2'}`}>
      <div className={`flex items-center justify-between ${globalCompactMode ? 'gap-2' : ''}`}>
        <div className={`flex items-center ${globalCompactMode ? 'gap-2' : isTightNav ? 'gap-2' : 'gap-4'}`}>
          <div className="flex items-center gap-2 shrink-0">
            <img
              src={logoIcon}
              alt="Patent Loop"
              className={`${globalCompactMode ? 'h-6 w-auto' : 'h-7 w-auto'} cursor-pointer hover:opacity-80 hover:scale-105 transition-all duration-200`}
              title="Click to open User Guide"
              onClick={handleOpenUserGuide}
            />
            <span className="px-2 py-0.5 text-[10px] font-bold whitespace-nowrap bg-gradient-to-r from-blue-500 via-blue-600 to-purple-600 text-white rounded-md shadow-md hover:shadow-lg transition-shadow">
              BETA
            </span>
          </div>

          {/* Quick Start Button */}
          <Button
            variant="ghost"
            size={globalCompactMode ? 'sm' : 'default'}
            onClick={handleOpenQuickStart}
            className={`${navButtonClass} shrink-0 whitespace-nowrap transition-all duration-200 border border-transparent hover:bg-gradient-to-br hover:from-purple-50 hover:to-purple-100/40 hover:text-purple-700 hover:shadow-md hover:border-purple-200/50`}
            title="Quick Start"
          >
            <Sparkles className={globalCompactMode ? 'w-3 h-3' : 'w-4 h-4'} />
            {!isTightNav && 'Quick Start'}
          </Button>

          {/* Full Navigation (>= 1150px) */}
          {!isCompactNav && (
            <nav className={`flex items-center ${isTightNav ? 'gap-0.5' : 'gap-1'}`}>
              <Button
                variant={activePage === 'chat' ? 'secondary' : 'ghost'}
                size={globalCompactMode ? 'sm' : 'default'}
                onClick={() => onPageChange('chat')}
                className={`${navButtonClass} transition-all duration-200 ${
                  activePage === 'chat'
                    ? 'bg-gradient-to-br from-blue-100 to-blue-200/70 text-blue-800 shadow-md border border-blue-300/80 font-semibold'
                    : 'border border-transparent hover:bg-gradient-to-br hover:from-blue-50 hover:to-blue-100/40 hover:text-blue-700 hover:shadow-md hover:border-blue-200/50'
                }`}
              >
                <MessageSquare className={globalCompactMode ? 'w-3 h-3' : 'w-4 h-4'} />
                Chat
              </Button>
              <Button
                variant={activePage === 'documents' ? 'secondary' : 'ghost'}
                size={globalCompactMode ? 'sm' : 'default'}
                onClick={() => onPageChange('documents')}
                className={`${navButtonClass} transition-all duration-200 ${
                  activePage === 'documents'
                    ? 'bg-gradient-to-br from-blue-100 to-blue-200/70 text-blue-800 shadow-md border border-blue-300/80 font-semibold'
                    : 'border border-transparent hover:bg-gradient-to-br hover:from-blue-50 hover:to-blue-100/40 hover:text-blue-700 hover:shadow-md hover:border-blue-200/50'
                }`}
              >
                <FileText className={globalCompactMode ? 'w-3 h-3' : 'w-4 h-4'} />
                Documents
              </Button>
              <Button
                variant={activePage === 'uspto_data' ? 'secondary' : 'ghost'}
                size={globalCompactMode ? 'sm' : 'default'}
                onClick={() => onPageChange('uspto_data')}
                className={`${navButtonClass} transition-all duration-200 ${
                  activePage === 'uspto_data'
                    ? 'bg-gradient-to-br from-blue-100 to-blue-200/70 text-blue-800 shadow-md border border-blue-300/80 font-semibold'
                    : 'border border-transparent hover:bg-gradient-to-br hover:from-blue-50 hover:to-blue-100/40 hover:text-blue-700 hover:shadow-md hover:border-blue-200/50'
                }`}
              >
                <Database className={globalCompactMode ? 'w-3 h-3' : 'w-4 h-4'} />
                USPTO Data
              </Button>
              <Button
                variant={activePage === 'examination' ? 'secondary' : 'ghost'}
                size={globalCompactMode ? 'sm' : 'default'}
                onClick={() => onPageChange('examination')}
                className={`${navButtonClass} transition-all duration-200 ${
                  activePage === 'examination'
                    ? 'bg-gradient-to-br from-blue-100 to-blue-200/70 text-blue-800 shadow-md border border-blue-300/80 font-semibold'
                    : 'border border-transparent hover:bg-gradient-to-br hover:from-blue-50 hover:to-blue-100/40 hover:text-blue-700 hover:shadow-md hover:border-blue-200/50'
                }`}
              >
                <GitBranch className={globalCompactMode ? 'w-3 h-3' : 'w-4 h-4'} />
                Examination
              </Button>
              <Button
                variant={activePage === 'resources' ? 'secondary' : 'ghost'}
                size={globalCompactMode ? 'sm' : 'default'}
                onClick={() => onPageChange('resources')}
                className={`${navButtonClass} transition-all duration-200 ${
                  activePage === 'resources'
                    ? 'bg-gradient-to-br from-blue-100 to-blue-200/70 text-blue-800 shadow-md border border-blue-300/80 font-semibold'
                    : 'border border-transparent hover:bg-gradient-to-br hover:from-blue-50 hover:to-blue-100/40 hover:text-blue-700 hover:shadow-md hover:border-blue-200/50'
                }`}
              >
                <BookOpen className={globalCompactMode ? 'w-3 h-3' : 'w-4 h-4'} />
                Resources
              </Button>
              <Button
                variant={activePage === 'tasks' ? 'secondary' : 'ghost'}
                size={globalCompactMode ? 'sm' : 'default'}
                onClick={() => onPageChange('tasks')}
                className={`${navButtonClass} transition-all duration-200 ${
                  activePage === 'tasks'
                    ? 'bg-gradient-to-br from-blue-100 to-blue-200/70 text-blue-800 shadow-md border border-blue-300/80 font-semibold'
                    : 'hover:bg-gradient-to-br hover:from-blue-50 hover:to-blue-100/40 hover:text-blue-700 hover:shadow-md hover:border hover:border-blue-200/50'
                }`}
              >
                <ListTodo className={globalCompactMode ? 'w-3 h-3' : 'w-4 h-4'} />
                Tasks
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant={(activePage === 'ids' || activePage === 'forms') ? 'secondary' : 'ghost'}
                    size={globalCompactMode ? 'sm' : 'default'}
                    className={`${navButtonClass} transition-all duration-200 ${
                      (activePage === 'ids' || activePage === 'forms')
                        ? 'bg-gradient-to-br from-purple-100 to-purple-200/70 text-purple-800 shadow-md border border-purple-300/80 font-semibold'
                        : 'hover:bg-gradient-to-br hover:from-purple-50 hover:to-purple-100/40 hover:text-purple-700 hover:shadow-md hover:border hover:border-purple-200/50'
                    }`}
                  >
                    <ClipboardList className={globalCompactMode ? 'w-3 h-3' : 'w-4 h-4'} />
                    IDS & Forms
                    <ChevronDown className={globalCompactMode ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="bg-white border border-gray-200 shadow-lg">
                  <DropdownMenuItem
                    onClick={() => onPageChange('ids')}
                    className={`cursor-pointer ${activePage === 'ids' ? 'bg-purple-50 font-medium' : ''}`}
                  >
                    <ClipboardList className="w-4 h-4 mr-2" />
                    IDS Generator
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onPageChange('forms')}
                    className={`cursor-pointer ${activePage === 'forms' ? 'bg-purple-50 font-medium' : ''}`}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    USPTO Forms
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {/* Office action analysis lives in /action now. The OA Templates page is
                  kept but unlinked; the /action tool reads the same templates. */}
              <Button
                variant="ghost"
                size={globalCompactMode ? 'sm' : 'default'}
                onClick={() => navigate('/action')}
                className={`${navButtonClass} transition-all duration-200 hover:bg-gradient-to-br hover:from-teal-50 hover:to-teal-100/40 hover:text-teal-700 hover:shadow-md hover:border hover:border-teal-200/50`}
              >
                <FileOutput className={globalCompactMode ? 'w-3 h-3' : 'w-4 h-4'} />
                OA Analysis
              </Button>
            </nav>
          )}

          {/* Compact Navigation Selector (< 1150px) */}
          {isCompactNav && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" className="gap-2">
                  <CurrentPageIcon className="w-4 h-4" />
                  {currentPageInfo.label}
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-white border border-gray-200 shadow-lg">
                <DropdownMenuItem
                  onClick={() => onPageChange('chat')}
                  className={`text-gray-700 hover:bg-gray-100 cursor-pointer ${activePage === 'chat' ? 'bg-blue-50' : ''}`}
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Chat
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onPageChange('documents')}
                  className={`text-gray-700 hover:bg-gray-100 cursor-pointer ${activePage === 'documents' ? 'bg-blue-50' : ''}`}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Documents
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onPageChange('uspto_data')}
                  className={`text-gray-700 hover:bg-gray-100 cursor-pointer ${activePage === 'uspto_data' ? 'bg-blue-50' : ''}`}
                >
                  <Database className="w-4 h-4 mr-2" />
                  USPTO Data
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onPageChange('examination')}
                  className={`text-gray-700 hover:bg-gray-100 cursor-pointer ${activePage === 'examination' ? 'bg-blue-50' : ''}`}
                >
                  <GitBranch className="w-4 h-4 mr-2" />
                  Examination
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onPageChange('resources')}
                  className={`text-gray-700 hover:bg-gray-100 cursor-pointer ${activePage === 'resources' ? 'bg-blue-50' : ''}`}
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Resources
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onPageChange('tasks')}
                  className={`text-gray-700 hover:bg-gray-100 cursor-pointer ${activePage === 'tasks' ? 'bg-blue-50' : ''}`}
                >
                  <ListTodo className="w-4 h-4 mr-2" />
                  Tasks
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-gray-500">IDS & Forms</DropdownMenuLabel>
                <DropdownMenuItem
                  onClick={() => onPageChange('ids')}
                  className={`text-gray-700 hover:bg-gray-100 cursor-pointer ${activePage === 'ids' ? 'bg-purple-50' : ''}`}
                >
                  <ClipboardList className="w-4 h-4 mr-2" />
                  IDS Generator
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onPageChange('forms')}
                  className={`text-gray-700 hover:bg-gray-100 cursor-pointer ${activePage === 'forms' ? 'bg-purple-50' : ''}`}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  USPTO Forms
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/action')}
                  className="text-gray-700 hover:bg-gray-100 cursor-pointer"
                >
                  <FileOutput className="w-4 h-4 mr-2" />
                  OA Analysis
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Desktop View (>= 1350px) - Show all items separately */}
          {!isCompactMode && (
            <>
              <Badge
                variant="outline"
                className="gap-1 bg-blue-50 text-blue-700 border-blue-200 cursor-pointer hover:bg-blue-100 hover:border-blue-300 transition-colors"
                onClick={() => setShowCreditsModal(true)}
                title="Click to view credit details"
              >
                <Coins className="w-3 h-3" />
                {creditsLoading ? (
                  'Loading...'
                ) : credits !== null ? (
                  `${credits.toLocaleString()} credits`
                ) : (
                  'Credits unavailable'
                )}
              </Badge>

              {renderDraftingMenu()}

              {/* Outstanding docket items. Red when something is overdue, otherwise
                  a neutral count — a permanent red badge stops meaning anything. */}

              <Button
                variant="ghost"
                size="icon"
                className="relative"
                onClick={() => navigate('/docket')}
                title={
                  docketOpen
                    ? `Docket — ${docketOpen} outstanding${docketOverdue ? `, ${docketOverdue} overdue` : ''}`
                    : 'Docket'
                }
              >
                <CalendarClock className="w-5 h-5" />
                {docketOpen > 0 && (
                  <span
                    className={`absolute top-1 right-1 min-w-[16px] h-4 px-1 text-white text-xs rounded-full flex items-center justify-center ${
                      docketOverdue > 0 ? 'bg-red-500' : 'bg-slate-500'
                    }`}
                  >
                    {docketOpen > 99 ? '99+' : docketOpen}
                  </span>
                )}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative" aria-label="Notifications">
                    <Bell className="w-5 h-5" />
                    {unreadSuccessCount > 0 && (
                      <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                        {unreadSuccessCount}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="p-0">
                  <NotificationDropdown />
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Account menu">
                    <User className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white border border-gray-200 shadow-lg">
                  <DropdownMenuLabel className="text-gray-900">
                    {user?.username || 'My Account'}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-gray-200" />
                  <DropdownMenuItem onClick={handleOpenSettings} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                    <User className="w-4 h-4 mr-2" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/docket')} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                    <CalendarClock className="w-4 h-4 mr-2" />
                    Docket
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/fortech')} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                    <Network className="w-4 h-4 mr-2" />
                    ForTech
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/drafting')} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                    <PenLine className="w-4 h-4 mr-2" />
                    Drafting
                  </DropdownMenuItem>
                  {isAdminUsername(user?.username) && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/admin/users')} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                      <ShieldCheck className="w-4 h-4 mr-2" />
                      User Management
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/admin/invoices')} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                      <ReceiptText className="w-4 h-4 mr-2" />
                      Invoicing
                    </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuItem onClick={handleOpenUserGuide} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                    <HelpCircle className="w-4 h-4 mr-2" />
                    User Guide
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleOpenFeedback} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Send Feedback
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleOpenSupportPanel} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                    <LifeBuoy className="w-4 h-4 mr-2" />
                    Support Request
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleOpenAbout} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                    <Info className="w-4 h-4 mr-2" />
                    About
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-gray-200" />
                  <DropdownMenuItem onClick={handleLogout} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                    <LogOut className="w-4 h-4 mr-2" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}

          {/* Compact View (< 1350px) - Three-dot menu */}
          {isCompactMode && (
            <>
              {renderDraftingMenu()}

              {/* Kept out of the overflow menu: an overdue count is the one thing
                  worth seeing without opening anything. */}

              <Button
                variant="ghost"
                size="icon"
                className="relative"
                onClick={() => navigate('/docket')}
                title={
                  docketOpen
                    ? `Docket — ${docketOpen} outstanding${docketOverdue ? `, ${docketOverdue} overdue` : ''}`
                    : 'Docket'
                }
              >
                <CalendarClock className="w-5 h-5" />
                {docketOpen > 0 && (
                  <span
                    className={`absolute top-1 right-1 min-w-[16px] h-4 px-1 text-white text-xs rounded-full flex items-center justify-center ${
                      docketOverdue > 0 ? 'bg-red-500' : 'bg-slate-500'
                    }`}
                  >
                    {docketOpen > 99 ? '99+' : docketOpen}
                  </span>
                )}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreVertical className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white border border-gray-200 shadow-lg w-56">
                <DropdownMenuLabel className="text-gray-900">
                  {user?.username || 'My Account'}
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-gray-200" />

                {/* Credits Display */}
                <div className="px-2 py-2">
                  <div
                    className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-100 cursor-pointer transition-colors"
                    onClick={() => setShowCreditsModal(true)}
                  >
                    <Coins className="w-4 h-4 text-blue-600" />
                    <span className="text-sm text-gray-700">
                      {creditsLoading ? (
                        'Loading...'
                      ) : credits !== null ? (
                        `${credits.toLocaleString()} credits`
                      ) : (
                        'Credits unavailable'
                      )}
                    </span>
                  </div>
                </div>

                <DropdownMenuSeparator className="bg-gray-200" />

                {/* Notifications - Show in compact menu as text, actual dropdown is separate */}
                <div className="px-2 py-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <div className="flex items-center justify-between w-full px-2 py-1.5 rounded hover:bg-gray-100 cursor-pointer transition-colors">
                        <div className="flex items-center">
                          <Bell className="w-4 h-4 mr-2 text-gray-700" />
                          <span className="text-sm text-gray-700">Notifications</span>
                        </div>
                        {unreadSuccessCount > 0 && (
                          <span className="min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                            {unreadSuccessCount}
                          </span>
                        )}
                      </div>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="p-0">
                      <NotificationDropdown />
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <DropdownMenuSeparator className="bg-gray-200" />

                {/* Account Options */}
                <DropdownMenuItem onClick={handleOpenSettings} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/profile')} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                  <User className="w-4 h-4 mr-2" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/docket')} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                  <CalendarClock className="w-4 h-4 mr-2" />
                  Docket
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/fortech')} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                  <Network className="w-4 h-4 mr-2" />
                  ForTech
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/drafting')} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                  <PenLine className="w-4 h-4 mr-2" />
                  Drafting
                </DropdownMenuItem>
                {isAdminUsername(user?.username) && (
                  <>
                    <DropdownMenuItem onClick={() => navigate('/admin/users')} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                    <ShieldCheck className="w-4 h-4 mr-2" />
                    User Management
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/admin/invoices')} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                    <ReceiptText className="w-4 h-4 mr-2" />
                    Invoicing
                  </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuItem onClick={handleOpenUserGuide} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                  <HelpCircle className="w-4 h-4 mr-2" />
                  User Guide
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleOpenFeedback} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Send Feedback
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleOpenSupportPanel} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                  <LifeBuoy className="w-4 h-4 mr-2" />
                  Support Request
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleOpenAbout} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                  <Info className="w-4 h-4 mr-2" />
                  About
                </DropdownMenuItem>

                <DropdownMenuSeparator className="bg-gray-200" />

                <DropdownMenuItem onClick={handleLogout} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                  <LogOut className="w-4 h-4 mr-2" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </>
          )}
        </div>
      </div>

      {/* Credits Modal */}
      <CreditsModal
        open={showCreditsModal}
        onOpenChange={setShowCreditsModal}
      />

      {/* Settings Modal */}
      <SettingsModal
        open={showSettingsModal}
        onOpenChange={setShowSettingsModal}
      />

      {/* Feedback Modal */}
      <FeedbackModal
        open={showFeedbackModal}
        onOpenChange={setShowFeedbackModal}
      />

      {/* About Modal */}
      <AboutModal
        open={showAboutModal}
        onOpenChange={setShowAboutModal}
      />

      {/* User Guide */}
      <UserGuide
        open={showUserGuide}
        onOpenChange={setShowUserGuide}
      />

      {/* Support Request Panel */}
      <SupportRequestPanel
        isOpen={showSupportPanel}
        onClose={() => setShowSupportPanel(false)}
      />
    </header>
  );
}
